import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useLocation } from '@tanstack/react-router'
import {
  canManageOwnSubscription,
  formatMoney,
  isCohortSession,
  ownedIds,
  useMyPurchases,
  joinDoor,
  joinLeadMinutes,
  nextJoinOpening,
  rsvpRefusal,
  useEvent,
  useEventCategories,
  useEvents,
  useToggleRsvp,
  type Event,
  type JoinDoor,
  type RsvpRefusal,
} from '@lb/domain'
import { useTranslation } from '@lb/i18n'
import {
  Button,
  EmptyState,
  IconAlert,
  IconCalendar,
  IconCrew,
  IconPlay,
  PageHeader,
  PhotoCover,
  Pill,
  Skeleton,
  cn,
} from '@lb/ui'

import { useSessionContext } from '../../app/providers/session'
import { http } from '../../lib/client'
import {
  formatDateShort,
  formatDayOfMonth,
  formatMonth,
  formatMonthShort,
  formatOrList,
  formatTime,
} from '../../lib/format'
import { cohortLine } from '../community/room-groups'
import { eventAction } from './event-action'

/**
 * Events (spec §8) — featured event, month-grouped list, RSVP.
 *
 * RSVP landed in round 6 and this stops being a read-only calendar.
 */
/**
 * `.ev-filters` — the design's pills, on the categories an editor made.
 *
 * They were our three `EventKind`s (webinar, live, gathering) until 25 Sep
 * 2026, when the back office got its own chips (`GET /events/categories`,
 * TSP request 4). The chips are the categories that have at least one event in
 * the current upcoming list, in the editor's order — a chip that can only
 * filter to nothing is a dead control — and a chosen chip filters on the
 * server (`?category=`), so it shows everything filed under it.
 */
const ALL = 'all'

export function EventsScreen() {
  const { t, i18n } = useTranslation()
  const { viewer } = useSessionContext()
  const [category, setCategory] = useState<string>(ALL)
  /*
   * Which of these places were BOUGHT (12 Oct 2026). A bought place writes the
   * same RSVP as a free one, so `myRsvp` cannot tell them apart — and the
   * difference matters at the one control on the card: « Je ne pourrai pas »
   * gives up a place somebody paid 25 $ for, with no refund behind it.
   *
   * Skipped for an entitled member, who cannot have needed a ticket.
   */
  const purchases = useMyPurchases(http, viewer?.entitlement !== 'entitled')
  const bought = ownedIds(purchases.data)
  const events = useEvents(http, 'upcoming', i18n.language)
  const categories = useEventCategories(http, i18n.language)
  /*
   * A SECOND query, for `past`.
   *
   * `/events` answers one window at a time — `?when=upcoming` or `?when=past` —
   * and the screen only ever asked for the first, so a recorded event could
   * never appear no matter what `replayRef` said. The replays grid is the
   * reason to ask for the other window, and it is a separate query rather than
   * a widened one so a slow archive never delays what is coming up.
   */
  const past = useEvents(http, 'past', i18n.language)
  const rsvp = useToggleRsvp(http, i18n.language)

  /*
   * Ask again at the minute a door opens.
   *
   * The join link is withheld by the server until 15 minutes before the start,
   * so a member who opened this page at 11:30 for a noon event would otherwise
   * sit on « the link will appear here » until they reloaded. One timer, for
   * the soonest opening among the events they RSVP'd to.
   */
  const queryClient = useQueryClient()
  const upcoming = events.data?.items

  /* The chips: categories present in the unfiltered upcoming list, in order. */
  const chips = useMemo(() => {
    const present = new Set((upcoming ?? []).map((event) => event.categoryId).filter(Boolean))
    return (categories.data ?? []).filter((c) => present.has(c.id))
  }, [upcoming, categories.data])
  /* A chosen chip that has since emptied falls back to « Tous » rather than
     leaving the member on a filter they can no longer see. */
  const active = chips.some((c) => c.id === category) ? category : ALL
  const filtered = useEvents(http, 'upcoming', i18n.language, {
    category: active === ALL ? null : active,
  })

  /*
   * « Voir dans Événements » from a cohort milestone lands here with
   * `#event-<id>`. The list renders after its read, so the browser's own jump
   * to the fragment has already missed; scroll once the card exists.
   */
  const hash = useLocation().hash.replace(/^#/, '')
  const listed = filtered.data
  useEffect(() => {
    if (!hash.startsWith('event-') || !listed) return
    document.getElementById(hash)?.scrollIntoView({ block: 'center' })
  }, [hash, listed])

  useEffect(() => {
    const wait = upcoming ? nextJoinOpening(upcoming) : null
    if (wait === null) return

    // `setTimeout` overflows past ~24.8 days and fires at once.
    const timer = setTimeout(
      () => void queryClient.invalidateQueries({ queryKey: ['events'] }),
      Math.min(wait, 2_000_000_000),
    )
    return () => clearTimeout(timer)
  }, [upcoming, queryClient])

  if (events.isPending) return <EventsSkeleton />

  if (events.isError) {
    return <EmptyState title={t('events.errorTitle')} body={t('events.errorBody')} />
  }

  const all = [...events.data.items].sort((a, b) => a.startsAt.localeCompare(b.startsAt))
  const items =
    active === ALL
      ? all
      : [...(filtered.data?.items ?? [])].sort((a, b) => a.startsAt.localeCompare(b.startsAt))
  const loadingChip = active !== ALL && filtered.isPending
  const featured = items.find((e) => e.featured)

  /*
   * `.ev-replays` — an event that has been recorded is no longer something to
   * RSVP to, it is something to watch. Splitting them keeps the calendar a
   * calendar: a past webinar sitting in "August" with a disabled RSVP button
   * is noise in the one list a member scans for what is coming.
   *
   * `replayRef` is the signal, not the date — an event can be over with no
   * recording yet, and that one belongs in neither list.
   */
  const replays = (past.data?.items ?? []).filter(
    // The chip applies here too, so « Ateliers » does not list another category's replays.
    (e) => e.replayRef !== null && (active === ALL || e.categoryId === active),
  )
  const rest = items.filter((e) => e.id !== featured?.id)
  const months = groupByMonth(rest, i18n.language)

  return (
    <div className="flex flex-col gap-gap-sec">
      <PageHeader
        title={t('events.title')}
        sub={t('events.intro')}
        tint="sage"
        icon={<IconCalendar size={24} title={null} />}
      />

      {/* `.ev-filters` — « Tous », then one chip per category that occurs. No
          row at all when there is nothing to choose between. */}
      {chips.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          <FilterChip active={active === ALL} onClick={() => setCategory(ALL)} label={t('events.all')} />
          {chips.map((c) => (
            <FilterChip
              key={c.id}
              active={active === c.id}
              onClick={() => setCategory(c.id)}
              label={c.label}
            />
          ))}
        </div>
      ) : null}

      {loadingChip ? (
        <Skeleton className="h-28 w-full" />
      ) : active !== ALL && filtered.isError ? (
        <EmptyState title={t('events.errorTitle')} body={t('events.errorBody')} />
      ) : items.length === 0 ? (
        <EmptyState title={t('events.emptyTitle')} body={t('events.emptyBody')} />
      ) : null}

      {featured ? (
        <EventCard
          event={featured}
          featured
          bought={bought.has(featured.id)}
          onRsvp={(on) => rsvp.mutate({ eventId: featured.id, on })}
          {...rsvpStateFor(rsvp, featured.id)}
        />
      ) : null}

      {replays.length > 0 ? (
        <section>
          <h2 className="mb-4 font-serif text-[24px] text-ink">{t('events.replays')}</h2>
          <ul className="grid gap-gap-card sm:grid-cols-2 lg:grid-cols-3">
            {replays.map((event) => (
              <li key={event.id}>
                <ReplayCard event={event} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {months.map(([month, list]) => (
        <section key={month}>
          <h2 className="mb-4 font-serif text-[24px] text-ink">{month}</h2>
          <ul className="flex flex-col gap-gap-card">
            {list.map((event) => (
              <li key={event.id}>
                <EventCard
                  event={event}
                  bought={bought.has(event.id)}
                  onRsvp={(on) => rsvp.mutate({ eventId: event.id, on })}
                  {...rsvpStateFor(rsvp, event.id)}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

/** `.pd-filter` — a pill. */
function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean
  onClick: () => void
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex min-h-[38px] items-center gap-2 rounded-full px-3.5 text-[13px] font-semibold transition-colors',
        active ? 'bg-ink text-cream' : 'bg-cream-dark text-ink-soft hover:text-ink',
      )}
    >
      {label}
    </button>
  )
}

/**
 * The RSVP state that belongs to ONE row.
 *
 * One mutation serves every row on the page, so its pending state and its
 * refusal belong to the row it was fired from — `variables` says which.
 * Handing the bare `isPending`/`error` to every card put « Toutes les places
 * sont prises. » under every event when one of them filled, and set every
 * button busy during any RSVP. Driven before the fix: a 409 on the Atelier
 * printed the line under the Cercle de parents at 0 / 25.
 */
export function rsvpStateFor(
  mutation: { isPending: boolean; error: unknown; variables?: { eventId: string } },
  eventId: string,
): { busy: boolean; error: unknown } {
  const mine = mutation.variables?.eventId === eventId
  return { busy: mine && mutation.isPending, error: mine ? mutation.error : null }
}

function EventCard({
  event,
  featured,
  bought,
  onRsvp,
  busy,
  error,
}: {
  event: Event
  featured?: boolean
  /** This place was paid for — see `EventsScreen`. */
  bought?: boolean
  onRsvp: (on: boolean) => void
  busy: boolean
  error: unknown
}) {
  const { t } = useTranslation()
  /*
   * « Complet » is the SERVER's answer now (12 Oct 2026). `soldOut` counts
   * confirmed places **plus unexpired holds** — a place is held the moment
   * somebody's payment page is minted — and the count here cannot see a hold,
   * so two buyers could both be told one place was left and one of them
   * refused after paying.
   *
   * The count stays as the fallback: `soldOut` is defaulted `false`, so a
   * server predating it would otherwise answer « places available » for a
   * capped event that is visibly full.
   */
  const refusal = rsvpRefusal(error)
  const door = joinDoor(event)
  /*
   * The member's own cohort session (TSP request 4). Nobody books a place in
   * their own programme: the API hands the link over without an RSVP, so the
   * card offers the link or says it is coming — never « J'y serai », and never
   * a places counter.
   */
  const session = isCohortSession(event)
  /* The editor's chip when there is one; the kind for an uncategorised event. */
  const label = event.categoryLabel ?? t(`events.kind.${event.kind}`)
  const action = (
    <EventActionControl event={event} bought={bought} onRsvp={onRsvp} busy={busy} />
  )

  /*
   * `.ev-featured` — the design gives the featured event a different SHAPE,
   * not a different border.
   *
   * A tinted artwork panel carries the date block and a "Featured · <kind>"
   * ribbon, and the body gets room for the summary and the attendance count.
   * We had it as an ordinary row with a terra outline, which made the one event
   * the team is pushing look like the three below it.
   */
  if (featured) {
    return (
      <article
        id={`event-${event.id}`}
        className="grid scroll-mt-6 overflow-hidden rounded-[var(--r-lg)] border border-border bg-surface md:grid-cols-[300px_1fr]"
      >
        {/* The event's own cover art, set in the back office (8 Oct 2026 —
            these always showed a brand-pool photo, so the team's uploads never
            appeared). The brand pool is only the fallback for an event with none. */}
        <div className="relative min-h-[180px] overflow-hidden bg-cream">
          <PhotoCover
            src={event.coverUrl ?? undefined}
            alt={event.coverAlt}
            seed={event.id}
            ratio="16/10"
            className="absolute inset-0"
          />
          <time
            dateTime={event.startsAt}
            className="absolute left-5 top-5 flex h-16 w-16 flex-col items-center justify-center rounded-[var(--r-sm)] bg-surface shadow-[0_6px_18px_-8px_rgb(31_45_58/0.4)]"
          >
            <span className="font-num text-[22px] font-bold tabular-nums leading-none text-ink">
              {formatDayOfMonth(event.startsAt)}
            </span>
            <span className="text-[11px] uppercase text-muted">
              {formatMonthShort(event.startsAt)}
            </span>
          </time>
          <span className="absolute bottom-5 left-5 rounded-full bg-ink px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-cream">
            {t('events.featured')} · {label}
          </span>
        </div>

        <div className="p-card-lg">
          {/* The title opens the event (ISS-20): « event details » had nowhere to go. */}
          <h2 className="font-serif text-[clamp(21px,2.4vw,26px)] font-medium leading-tight text-ink">
            <Link to="/events/$id" params={{ id: event.id }} className="hover:underline">
              {event.title}
            </Link>
          </h2>
          <p className="mt-1.5 text-[13.5px] font-semibold text-ink">
            {formatDateShort(event.startsAt)} · {formatTime(event.startsAt)}
            {durationMinutes(event) !== null
              ? ` · ${t('events.minutes', { count: durationMinutes(event)! })}`
              : ''}
          </p>
          {event.summary ? (
            <p className="mt-3 max-w-[62ch] text-[14.5px] leading-[1.55] text-muted">
              {event.summary}
            </p>
          ) : null}

          {/*
           * "N going", which the design puts here and we were not rendering at
           * all — `rsvpCount` is counted on read and was only ever used to
           * work out whether the event was full. The count is the social
           * signal; "38 / 40 spots" is a different, colder fact, and it stays
           * below where capacity actually matters.
           */}
          <SessionLine event={event} className="mt-3" />

          <p className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-muted">
            <HostLine event={event} />
            {session ? null : <span>{t('events.going', { count: event.rsvpCount })}</span>}
            {!session && event.rsvpCapacity !== null ? (
              <span>{t('events.seats', { taken: event.rsvpCount, capacity: event.rsvpCapacity })}</span>
            ) : null}
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            {action}
            <a
              href={icsHref(event)}
              download={`${event.title}.ics`}
              className="inline-flex min-h-[44px] items-center text-[13px] font-semibold text-ink underline"
            >
              {t('events.addToCalendar')}
            </a>
          </div>

          {session ? <SessionLinkLine event={event} /> : <JoinLine event={event} door={door} />}
          <RsvpRefusalLine refusal={refusal} />
        </div>
      </article>
    )
  }

  return (
    <article
      id={`event-${event.id}`}
      className="scroll-mt-6 rounded-[var(--r-lg)] border border-border bg-surface p-card"
    >
      {/*
       * On a phone the row stacks (8 Oct 2026): the cover full width with the
       * date on it, then the text at full width, then the actions. Side by
       * side on a 375px screen left the title a word per line. From `md` up it
       * is the row it always was — the wrapper drops out with `md:contents`.
       */}
      <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-start">
        <div className="relative md:contents">
          {/* Every row shows its cover too. 16:9, the shape the back office
              asks for: the team's covers are posters with words on them, and a
              small square crop cut the words off. */}
          <span className="block w-full overflow-hidden rounded-[var(--r)] md:h-14 md:w-[100px] md:shrink-0 md:rounded-[var(--r-sm)]">
            <PhotoCover src={event.coverUrl ?? undefined} alt={event.coverAlt} seed={event.id} ratio="16/9" />
          </span>
          <time
            dateTime={event.startsAt}
            className="absolute left-3 top-3 flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-[var(--r-sm)] bg-surface shadow-[0_6px_18px_-8px_rgb(31_45_58/0.4)] md:static md:bg-cream-dark md:shadow-none"
          >
            <span className="font-num text-[18px] tabular-nums leading-none text-ink">
              {formatDayOfMonth(event.startsAt)}
            </span>
            <span className="text-[11px] uppercase text-muted">
              {formatMonthShort(event.startsAt)}
            </span>
          </time>
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink">
            {label}
            {' · '}
            {formatTime(event.startsAt)}
            {/* `· 45 min` — the design's row says how long it runs, not only
                when it starts. */}
            {durationMinutes(event) !== null
              ? ` · ${t('events.minutes', { count: durationMinutes(event)! })}`
              : ''}
          </p>
          <h3 className="mt-1 font-serif text-[20px] leading-tight text-ink">
            <Link to="/events/$id" params={{ id: event.id }} className="hover:underline">
              {event.title}
            </Link>
          </h3>
          {event.host || event.summary ? (
            <p className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[14px] text-muted">
              <HostLine event={event} />
              {event.summary ? <span>{event.summary}</span> : null}
            </p>
          ) : null}

          <SessionLine event={event} className="mt-2" />

          {/* « x / y places » only where there is a capacity to count against —
              never on an unlimited event, never on a cohort session. */}
          {!session && event.rsvpCapacity !== null ? (
            <p className="mt-2 text-[12px] text-muted">
              {t('events.seats', { taken: event.rsvpCount, capacity: event.rsvpCapacity })}
            </p>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3 md:flex-col md:items-end md:justify-start md:gap-2">
          {action}

          {/* An .ics is generated client-side — nothing missing from the API. */}
          <a
            href={icsHref(event)}
            download={`${event.title}.ics`}
            className="inline-flex min-h-[44px] items-center text-[12px] text-ink underline"
          >
            {t('events.addToCalendar')}
          </a>
        </div>
      </div>

      {session ? <SessionLinkLine event={event} /> : <JoinLine event={event} door={door} />}
      <RsvpRefusalLine refusal={refusal} />
    </article>
  )
}

/**
 * The one control an event card carries, whatever state it is in.
 *
 * One component for the list row, the event page's rail and Home's « À venir »
 * — because the branch had been written three times by 13 Oct and Home's copy
 * was missing the `ticket` case, so a member who had PAID for a place was
 * offered « Je ne pourrai pas » on their own home page.
 *
 * `eventAction` is the decision and is tested on its own; this is only how the
 * decision looks.
 */
export function EventActionControl({
  event,
  bought,
  locked,
  onRsvp,
  busy,
  size,
  full: fullWidth,
  signUpLabel,
}: {
  event: Event
  bought?: boolean
  /**
   * The lock the CALLER read. Home reads it through `isLocked`, which falls
   * back to the derivation for a server that sends no `locked`; the events
   * reads always carry the field, so they leave it off and the row answers.
   */
  locked?: boolean
  onRsvp: (on: boolean) => void
  busy: boolean
  size?: 'sm'
  full?: boolean
  /** Home says « Je m'inscris » where the calendar says « J'y serai ». */
  signUpLabel?: string
}) {
  const { t, i18n } = useTranslation()
  const width = fullWidth ? 'w-full' : undefined

  switch (eventAction(event, { bought, ...(locked === undefined ? {} : { locked }) })) {
    case 'session':
    case 'join':
      return <JoinButton event={event} />

    case 'ticket':
      /*
       * ISS-20: this said « Billet » and did nothing, so the one member who
       * had paid was the one with nowhere to go. It opens the event's own
       * page, where the ticket, its window and the join link live.
       */
      return (
        <Button asChild variant="secondary" size={size} className={width}>
          <Link to="/events/$id" params={{ id: event.id }}>
            {t('events.ticket')}
          </Link>
        </Button>
      )

    case 'soldOut':
      /*
       * « Complet » is final and « Acheter un billet » is not: a sold-out
       * event has no place to sell, so the button says so and does nothing
       * rather than sending somebody to a payment page that refuses them.
       */
      return (
        <Button variant="secondary" size={size} disabled className={width}>
          {t('events.full')}
        </Button>
      )

    case 'buy':
      return (
        <Button asChild size={size} className={width}>
          <Link to="/buy/$segment/$id" params={{ segment: 'events', id: event.id }}>
            {t('events.buyTicket', {
              price: formatMoney(
                { cents: event.priceCents as number, currency: 'CAD' },
                i18n.language,
              ),
            })}
          </Link>
        </Button>
      )

    case 'locked':
      /* Locked with no price: the membership is the only way in. */
      return (
        <Button asChild variant="secondary" size={size} className={width}>
          <Link to="/plans">{t('resources.seePlans')}</Link>
        </Button>
      )

    default:
      return (
        <Button
          className={width}
          size={size}
          variant={event.myRsvp ? 'secondary' : 'primary'}
          onClick={() => onRsvp(!event.myRsvp)}
          loading={busy}
          loadingLabel={t('state.busy')}
        >
          {event.myRsvp ? t('events.rsvpNo') : (signUpLabel ?? t('events.rsvpYes'))}
        </Button>
      )
  }
}

/**
 * « Séance de votre cohorte · Cohorte 2026 · Pod Nord » — says why this card
 * has no RSVP. Nothing on an ordinary event.
 */
function SessionLine({ event, className }: { event: Event; className?: string }) {
  const { t } = useTranslation()
  if (!isCohortSession(event)) return null
  const who = cohortLine(event)

  return (
    <p className={cn('flex flex-wrap items-center gap-2 text-[12.5px] text-ink-soft', className)}>
      <span className="whitespace-nowrap rounded-full bg-bleu-gris-light px-2.5 py-0.5 text-[11px] font-bold text-ink-on-tint">
        {t('events.sessionTag')}
      </span>
      {who ? <span>{who}</span> : null}
    </p>
  )
}

/** A cohort session whose link has not been set yet — say so rather than offer an RSVP. */
function SessionLinkLine({ event }: { event: Event }) {
  const { t } = useTranslation()
  if (event.joinUrl) return null

  return (
    <p className="mt-3 flex items-start gap-2 text-[13px] text-muted">
      <IconCalendar size={16} title={null} className="mt-[2px] shrink-0" />
      <span>{t('events.sessionNoLink')}</span>
    </p>
  )
}

/**
 * The way in, once the server has handed the link over.
 *
 * It takes the RSVP button's place rather than sitting beside it: in the last
 * fifteen minutes the member's question is « where do I click », and « I can't
 * make it » next to the answer is a mis-tap away from losing the link.
 *
 * A new tab, so the events page is still there when the call ends. A Zoom or
 * Vimeo link hands off to its own app from there when one is installed.
 */
function JoinButton({ event }: { event: Event }) {
  const { t } = useTranslation()
  if (!event.joinUrl) return null
  const href = event.joinUrl

  return (
    <Button variant="primary" onClick={() => window.open(href, '_blank', 'noopener,noreferrer')}>
      <IconPlay size={16} title={null} />
      {t('events.join')}
    </Button>
  )
}

/** What the member is told about the link while it is not theirs yet. */
function JoinLine({ event, door }: { event: Event; door: JoinDoor }) {
  const { t } = useTranslation()
  if (door !== 'waiting' && door !== 'rsvpFirst') return null

  const minutes = joinLeadMinutes(event) ?? 15

  return (
    <p className="mt-3 flex items-start gap-2 text-[13px] text-muted">
      <IconCalendar size={16} title={null} className="mt-[2px] shrink-0" />
      <span>{t(door === 'waiting' ? 'events.joinWaiting' : 'events.joinRsvpFirst', { minutes })}</span>
    </p>
  )
}

/**
 * Why an RSVP did not go through, under the row it came from.
 *
 * The capacity refusal names its numbers; the plan refusal names the plans. A
 * member meets the plan refusal only from a page that went stale — their plan
 * stopped qualifying after the list loaded — so the sentence explains what
 * changed rather than pretending the event vanished.
 *
 * The « Changer de forfait » link is offered only to a member who holds their
 * own subscription. A seat holder's plan is their employer's: the sentence still
 * names it, and offers no action they cannot take. `rsvpRefusal` has already
 * dropped archived plans, so the link never points at one that is not on sale.
 */
export function RsvpRefusalLine({ refusal }: { refusal: RsvpRefusal | null }) {
  const { t } = useTranslation()
  const { viewer } = useSessionContext()
  if (!refusal) return null

  const reserved = refusal.key === 'events.reservedFor'
  return (
    <p
      role="alert"
      className="mt-3 flex items-start gap-2 rounded-[var(--r-sm)] bg-error-surface px-3 py-2 text-[13px] text-error"
    >
      <IconAlert size={16} title={null} className="mt-px shrink-0" />
      <span>
        {reserved
          ? t('events.reservedFor', { plans: formatOrList(refusal.plans ?? []) })
          : t(refusal.key)}
        {reserved && canManageOwnSubscription(viewer?.access) ? (
          <>
            {' '}
            <Link to="/billing/change-plan" className="font-semibold underline">
              {t('events.changePlan')}
            </Link>
          </>
        ) : null}
      </span>
    </p>
  )
}

/**
 * `45 min` — the length the design puts beside the time.
 *
 * `endsAt` is nullable, so an open-ended session simply says when it starts.
 * Never zero: a same-instant pair is bad data, not a zero-length session, and
 * printing "0 min" would state it as a fact.
 */
function durationMinutes(event: Event): number | null {
  if (!event.endsAt) return null
  const minutes = Math.round((Date.parse(event.endsAt) - Date.parse(event.startsAt)) / 60_000)
  return minutes > 0 ? minutes : null
}

/**
 * `👤 Krysta Letto` — who is running it.
 *
 * The design puts this on every row and on the featured card, and it was the
 * one thing the card could not say: the enrichment arrives as `host` and our
 * schema called it `author`, so it was parsed away at the boundary.
 */
function HostLine({ event, className }: { event: Event; className?: string }) {
  if (!event.host) return null
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <IconCrew size={12} title={null} className="text-slate" />
      {event.host.fullName}
      {event.host.credential ? `, ${event.host.credential}` : ''}
    </span>
  )
}

function groupByMonth(events: Event[], _language: string): [string, Event[]][] {
  const groups = new Map<string, Event[]>()
  for (const event of events) {
    // `_language` is unused but declared: it is what makes the memo-free
    // regrouping re-run when the member switches language, since the month
    // labels below come from the active locale.
    const label = formatMonth(event.startsAt)
    const existing = groups.get(label)
    if (existing) existing.push(event)
    else groups.set(label, [event])
  }
  return [...groups.entries()]
}

/** A minimal VEVENT, built from fields the API already gives us. */
function icsHref(event: Event): string {
  const stamp = (iso: string) => iso.replace(/[-:]/g, '').replace(/\.\d{3}/, '')
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'BEGIN:VEVENT',
    `UID:${event.id}`,
    `DTSTART:${stamp(event.startsAt)}`,
    ...(event.endsAt ? [`DTEND:${stamp(event.endsAt)}`] : []),
    `SUMMARY:${event.title}`,
    // Never the join link: it is withheld until the door opens, and a calendar
    // entry saved a week ahead would be the way around that. The entry brings
    // the member back here, where the Join button will be.
    `URL:${window.location.origin}/events`,
    'END:VEVENT',
    'END:VCALENDAR',
  ]
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(lines.join('\r\n'))}`
}

function EventsSkeleton() {
  return (
    <div className="flex flex-col gap-gap-card">
      <Skeleton className="h-10 w-56" />
      {Array.from({ length: 3 }, (_, i) => (
        <Skeleton key={i} className="h-28 w-full" />
      ))}
    </div>
  )
}

/**
 * `.ev-replay` — a recorded event.
 *
 * No RSVP: the thing has happened. The card's whole job is "watch this", so it
 * carries the play affordance and the date it took place, and nothing that
 * implies a decision is still open.
 *
 * `replayRef` is a Vimeo reference like every other video on this platform, so
 * the destination is the same player the rest of the app uses rather than a
 * second embed path.
 */
function ReplayCard({ event }: { event: Event }) {
  const { t } = useTranslation()

  return (
    <article className="lb-lift flex h-full flex-col overflow-hidden rounded-[var(--r-lg)] border border-border bg-surface">
      <div className="relative grid aspect-video place-items-center bg-cream-dark">
        <span className="grid h-10 w-10 place-items-center rounded-full bg-[rgb(41_51_57/0.88)] text-on-surface-ink">
          <IconPlay size={15} title={null} />
        </span>
        {/* The scrim is the fixed-dark surface, so the text on it is the fixed
            light one. `text-cream` flips to the dark canvas in dark mode and
            went dark-on-dark at 1.12:1. */}
        <span className="absolute bottom-2.5 end-2.5 rounded-full bg-surface-ink/80 px-2 py-0.5 text-[11px] font-semibold text-on-surface-ink">
          {t('events.replayTag')}
        </span>
      </div>
      <div className="flex flex-1 flex-col px-5 pb-5 pt-4">
        <h3 className="font-serif text-[17px] font-medium leading-snug text-ink">{event.title}</h3>
        <p className="mt-auto pt-2.5 text-[12.5px] text-muted">
          {formatDateShort(event.startsAt)}
        </p>
      </div>
    </article>
  )
}

/* ─────────────────────────────────────────────────────────────────────────
 * One event, at `/events/$id` — ISS-10 and ISS-20, 13 Oct 2026.
 *
 * ── Why it did not exist, and what that cost ─────────────────────────────
 * `content-routes.ts` has always mapped the API's `events` segment to
 * `/events`, so `routeForContent('events', id)` minted `/events/<uuid>` — and
 * the router declared `/events` and nothing under it. Eight surfaces call
 * those mappers (search, Home's « Pour vous », the browse shelves, a
 * collection's shelf, the cohort links, the journey stops, the resume cards),
 * so **every one of them answered Not Found on an event**. That is ISS-10,
 * including its "a card led to /events/25d79ec4-…" half: not a wrong link on
 * one card, the one mapping with nothing behind it.
 *
 * It is also ISS-20: the list's « Billet » was a `Pill`, the title was not a
 * link, and a member who had paid for a place had nowhere to go to look at it.
 *
 * ── It reuses the card rather than restating it ──────────────────────────
 * `joinDoor`, `rsvpRefusal`, `isCohortSession`, `JoinButton`, `JoinLine`,
 * `RsvpRefusalLine`, `icsHref` — the branching an event needs is already
 * written and already driven. What this screen adds is the room to say more:
 * the ticket, the window it is open for, and the summary at full width.
 * ───────────────────────────────────────────────────────────────────────── */

export function EventScreen({ id }: { id: string }) {
  const { t, i18n } = useTranslation()
  const { viewer } = useSessionContext()
  const event = useEvent(http, id, i18n.language)
  const rsvp = useToggleRsvp(http, i18n.language)
  /* The same read the list uses, so a ticket is known here too. */
  const purchases = useMyPurchases(http, Boolean(viewer) && viewer?.entitlement !== 'entitled')

  if (event.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-[280px] w-full rounded-[var(--r-lg)]" />
      </div>
    )
  }

  /*
   * A locked event still answers 200 with its teaser, so this is genuinely
   * gone — unpublished, archived, or an id from somewhere else. Not a paywall.
   */
  if (event.isError || !event.data) {
    return (
      <EmptyState
        title={t('events.notFoundTitle')}
        body={t('events.notFoundBody')}
        action={
          <Button asChild variant="secondary">
            <Link to="/events">{t('events.backToEvents')}</Link>
          </Button>
        }
      />
    )
  }

  const row = event.data
  const bought = ownedIds(purchases.data).has(row.id)
  const session = isCohortSession(row)
  const door = joinDoor(row)
  const { busy, error } = rsvpStateFor(rsvp, row.id)
  const refusal = rsvpRefusal(error)
  const label = row.categoryLabel ?? t(`events.kind.${row.kind}`)
  const minutes = durationMinutes(row)
  const mine = purchases.data?.items.find((item) => item.contentItemId === row.id) ?? null

  return (
    <article className="flex flex-col gap-5">
      <Link
        to="/events"
        className="inline-flex w-fit items-center gap-1.5 text-[13px] font-semibold text-ink hover:text-ink-soft"
      >
        <span aria-hidden>‹</span>
        {t('events.backToEvents')}
      </Link>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-5">
          <div className="relative overflow-hidden rounded-[var(--r-lg)] border border-border">
            <PhotoCover src={row.coverUrl ?? undefined} alt={row.coverAlt} seed={row.id} ratio="16/9" />
            <time
              dateTime={row.startsAt}
              className="absolute left-5 top-5 flex h-16 w-16 flex-col items-center justify-center rounded-[var(--r-sm)] bg-surface shadow-[0_6px_18px_-8px_rgb(31_45_58/0.4)]"
            >
              <span className="font-num text-[22px] font-bold tabular-nums leading-none text-ink">
                {formatDayOfMonth(row.startsAt)}
              </span>
              <span className="text-[11px] uppercase text-muted">{formatMonthShort(row.startsAt)}</span>
            </time>
          </div>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink">{label}</p>
            <h1 className="mt-1.5 font-serif text-[clamp(24px,3vw,34px)] font-medium leading-[1.1] text-ink">
              {row.title}
            </h1>
            <p className="mt-2 text-[14px] font-semibold text-ink">
              {formatDateShort(row.startsAt)} · {formatTime(row.startsAt)}
              {minutes !== null ? ` · ${t('events.minutes', { count: minutes })}` : ''}
            </p>
            <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-muted">
              <HostLine event={row} />
              {session ? null : <span>{t('events.going', { count: row.rsvpCount })}</span>}
              {!session && row.rsvpCapacity !== null ? (
                <span>{t('events.seats', { taken: row.rsvpCount, capacity: row.rsvpCapacity })}</span>
              ) : null}
            </p>
            <SessionLine event={row} className="mt-3" />
          </div>

          {row.summary ? (
            <p className="max-w-[68ch] text-[15px] leading-[1.6] text-ink-soft">{row.summary}</p>
          ) : null}
        </div>

        {/*
         * The rail is the decision — one card, whatever state the member is
         * in. It is sticky for the same reason the course outline is: the
         * summary scrolls and the way in should not.
         */}
        <aside className="lg:sticky lg:top-[88px]">
          <div className="rounded-[var(--r-lg)] border border-border bg-surface p-card">
            {/*
             * A place that was PAID FOR. The ticket leads, and the RSVP toggle
             * is deliberately absent — one tap from « Je ne pourrai pas »
             * would hand back a place that is not refundable, which is a
             * support conversation rather than a button (12 Oct 2026).
             */}
            {bought ? (
              <div className="mb-3 flex flex-col gap-1.5">
                <Pill tone="navy">{t('events.ticket')}</Pill>
                {mine ? (
                  <p className="text-[12px] text-muted">
                    {mine.accessEndsAt === null
                      ? t('home.lifetime')
                      : t('home.accessUntil', { date: formatDateShort(mine.accessEndsAt) })}
                  </p>
                ) : null}
              </div>
            ) : null}

            {/* The ticket is drawn above; here the control would repeat it. */}
            {bought && eventAction(row, { bought }) === 'ticket' ? null : (
              <EventActionControl
                event={row}
                bought={bought}
                onRsvp={(on) => rsvp.mutate({ eventId: row.id, on })}
                busy={busy}
                full
              />
            )}

            <a
              href={icsHref(row)}
              download={`${row.title}.ics`}
              className="mt-3 inline-flex min-h-[44px] items-center text-[13px] text-ink underline"
            >
              {t('events.addToCalendar')}
            </a>

            {/*
             * The join link, honestly. ISS-20's own event has `join_url = NULL`
             * in production — the server withholds `joinUrl` behind the paywall
             * and there is nothing behind it to withhold — so « the link is not
             * published yet » is a real state and not an error. The one thing
             * not to draw here is a button that does nothing.
             */}
            {session ? <SessionLinkLine event={row} /> : <JoinLine event={row} door={door} />}
            {!session && door === 'none' && !row.hasJoinLink ? (
              <p className="mt-3 flex items-start gap-2 text-[13px] text-muted">
                <IconCalendar size={16} title={null} className="mt-[2px] shrink-0" />
                <span>{t('events.noLinkYet')}</span>
              </p>
            ) : null}

            <RsvpRefusalLine refusal={refusal} />
          </div>
        </aside>
      </div>
    </article>
  )
}
